import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, IconButton, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import type { SystemAnnouncement } from '../../types/SystemAnnouncement';

const POLL_INTERVAL_MS = 30_000;

function severityToAlert(severity: string): 'info' | 'warning' | 'error' {
  switch (severity) {
    case 'CRITICAL':
      return 'error';
    case 'WARNING':
      return 'warning';
    default:
      return 'info';
  }
}

const AnnouncementBanners: React.FC = () => {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState<SystemAnnouncement[]>([]);
  const [locallyDismissedIds, setLocallyDismissedIds] = useState<Set<number>>(new Set());

  const fetchAnnouncements = useCallback(async () => {
    if (!user) {
      setAnnouncements([]);
      return;
    }
    try {
      const response = await api.get<SystemAnnouncement[]>('/announcements/active');
      setAnnouncements(Array.isArray(response.data) ? response.data : []);
    } catch {
      // Keep existing banners on transient failures.
    }
  }, [user]);

  useEffect(() => {
    setLocallyDismissedIds(new Set());
    void fetchAnnouncements();
  }, [user?.id, fetchAnnouncements]);

  useEffect(() => {
    if (!user) return undefined;

    const interval = window.setInterval(() => {
      void fetchAnnouncements();
    }, POLL_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void fetchAnnouncements();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user, fetchAnnouncements]);

  const handleDismiss = async (announcement: SystemAnnouncement) => {
    setLocallyDismissedIds((prev) => new Set(prev).add(announcement.id));
    try {
      await api.post(`/announcements/${announcement.id}/dismiss`);
    } catch {
      setLocallyDismissedIds((prev) => {
        const next = new Set(prev);
        next.delete(announcement.id);
        return next;
      });
    }
  };

  const visible = announcements.filter((a) => !locallyDismissedIds.has(a.id));
  if (!user || visible.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 64,
        left: 0,
        right: 0,
        zIndex: 1990,
        px: 2,
        pt: 1,
        pointerEvents: 'none',
      }}
    >
      <Stack spacing={1} sx={{ maxWidth: 960, mx: 'auto' }}>
        {visible.map((announcement) => (
          <Alert
            key={announcement.id}
            severity={severityToAlert(announcement.severity)}
            variant="filled"
            sx={{ pointerEvents: 'auto', boxShadow: 3 }}
            action={
              announcement.dismissible !== false ? (
                <IconButton
                  aria-label="dismiss announcement"
                  color="inherit"
                  size="small"
                  onClick={() => void handleDismiss(announcement)}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              ) : undefined
            }
          >
            {announcement.title ? (
              <>
                <strong>{announcement.title}</strong>
                {' — '}
                {announcement.message}
              </>
            ) : (
              announcement.message
            )}
          </Alert>
        ))}
      </Stack>
    </Box>
  );
};

export default AnnouncementBanners;
